import { clamp, lerp, FACE_GEOMETRY_ANCHORS } from './helpers'
import {
  createPointArray,
  canonicalToCanvas,
  applyFeatureAwareWrap
} from './faceMath'
import { applyCanonicalMouthDeformation } from './mouthModel'

export function createMeshWarper() {
  let targetCanonical = null
  let targetCanonicalSmoothed = null
  let fittedCanonical = null
  let warpCanonicalBuffer = null
  let geometryCanonicalBuffer = null
  let geometryCanonicalSmoothed = null

  const computeWarp = ({
    settings,
    avatarRig,
    liveCanonical,
    baselineCanonical,
    faceGeometryEngine,
    faceMetrics,
    canvasWidth,
    canvasHeight,
    liveRigMetrics,
    mouthLevel,
    jawOpen = 0,
    blendshapeSignals = null,
    featureWrapProfile,
    showAvatarEyes
  }) => {
    const resolvedCount =
      Math.min(
        avatarRig.meshCount || 0,
        liveCanonical?.length || 0,
        baselineCanonical?.length || 0
      ) ||
      avatarRig.meshCount ||
      0
    if (!resolvedCount)
      return { warpCanonical: null, eyeSocketDstPoints: null, faceGeometryPoseMatrix: null }
    const meshReady = !!(liveCanonical && baselineCanonical && faceMetrics)

    if (!targetCanonical || targetCanonical.length !== resolvedCount)
      targetCanonical = createPointArray(resolvedCount)
    if (!targetCanonicalSmoothed || targetCanonicalSmoothed.length !== resolvedCount)
      targetCanonicalSmoothed = createPointArray(resolvedCount)
    if (!fittedCanonical || fittedCanonical.length !== resolvedCount)
      fittedCanonical = createPointArray(resolvedCount)
    if (!warpCanonicalBuffer || warpCanonicalBuffer.length !== resolvedCount)
      warpCanonicalBuffer = createPointArray(resolvedCount)

    let appliedFaceGeometry = false
    let currentFaceGeometryPoseMatrix = null
    // Direct camera-mesh transfer mode: move avatar canonical points by
    // full live-vs-baseline deltas instead of tiny expression-weight scaling.
    const fullMeshRetarget = settings.fullMeshRetarget !== false

    if (liveCanonical && baselineCanonical) {
      const strength = fullMeshRetarget
        ? clamp(Number(settings.fullMeshRetargetStrength) || 1, 0.4, 1.4)
        : clamp(Number(settings.expressionStrength) || 0, 0, 2.5)
      const transferGain = fullMeshRetarget
        ? clamp(Number(settings.fullMeshRetargetGain) || 1, 0.5, 1.5)
        : 1
      const fitStrength = clamp(Number(settings.faceFitStrength) || 0.62, 0, 1)
      let motionEnergy = 0
      for (let i = 0; i < resolvedCount; i += 1) {
        const avatarBase = avatarRig.canonical[i]
        const live = liveCanonical[i]
        const baseLine = baselineCanonical[i]
        const weight = avatarRig.expressionWeights?.[i] ?? 0.4
        const maxDelta = fullMeshRetarget ? 0.18 : 0.06 + weight * 0.22
        const fitX = lerp(avatarBase.x, baseLine.x, fitStrength)
        const fitY = lerp(avatarBase.y, baseLine.y, fitStrength)
        const fitZ = lerp(avatarBase.z || 0, baseLine.z || 0, fitStrength * 0.8)
        fittedCanonical[i].x = fitX
        fittedCanonical[i].y = fitY
        fittedCanonical[i].z = fitZ

        const dx = clamp((live.x - baseLine.x) * transferGain, -maxDelta, maxDelta)
        const dy = clamp((live.y - baseLine.y) * transferGain, -maxDelta, maxDelta)
        const dz = clamp(((live.z || 0) - (baseLine.z || 0)) * transferGain, -maxDelta, maxDelta)
        if (fullMeshRetarget) {
          motionEnergy += Math.abs(dx) + Math.abs(dy)
          targetCanonical[i].x = avatarBase.x + dx * strength
          targetCanonical[i].y = avatarBase.y + dy * strength
          targetCanonical[i].z = (avatarBase.z || 0) + dz * strength
        } else {
          motionEnergy += (Math.abs(dx) + Math.abs(dy)) * weight
          targetCanonical[i].x = fitX + dx * strength * weight
          targetCanonical[i].y = fitY + dy * strength * weight
          targetCanonical[i].z = fitZ + dz * strength * weight
        }
      }

      const normalizedEnergy = motionEnergy / Math.max(1, resolvedCount)
      const baseSmoothAlpha = Number.isFinite(Number(settings.warpSmoothing))
        ? Math.min(1, Math.max(0.08, 1 - Number(settings.warpSmoothing)))
        : normalizedEnergy > 0.018
          ? 0.58
          : 0.38
      const boost = Math.min(1, Math.max(0, normalizedEnergy / 0.05))
      const smoothAlpha = Math.min(1, Math.max(0.08, baseSmoothAlpha * (1 - boost) + 0.9 * boost))
      // seeded smoothing
      if (!targetCanonicalSmoothed || targetCanonicalSmoothed.length !== resolvedCount) {
        for (let i = 0; i < resolvedCount; i += 1) {
          targetCanonicalSmoothed[i].x = targetCanonical[i].x
          targetCanonicalSmoothed[i].y = targetCanonical[i].y
          targetCanonicalSmoothed[i].z = targetCanonical[i].z || 0
        }
      } else {
        for (let i = 0; i < resolvedCount; i += 1) {
          targetCanonicalSmoothed[i].x = lerp(
            targetCanonicalSmoothed[i].x,
            targetCanonical[i].x,
            smoothAlpha
          )
          targetCanonicalSmoothed[i].y = lerp(
            targetCanonicalSmoothed[i].y,
            targetCanonical[i].y,
            smoothAlpha
          )
          targetCanonicalSmoothed[i].z = lerp(
            targetCanonicalSmoothed[i].z || 0,
            targetCanonical[i].z || 0,
            smoothAlpha
          )
        }
      }
    } else {
      for (let i = 0; i < resolvedCount; i += 1) {
        targetCanonical[i].x = avatarRig.canonical[i].x
        targetCanonical[i].y = avatarRig.canonical[i].y
        targetCanonical[i].z = avatarRig.canonical[i].z || 0
        fittedCanonical[i].x = avatarRig.canonical[i].x
        fittedCanonical[i].y = avatarRig.canonical[i].y
        fittedCanonical[i].z = avatarRig.canonical[i].z || 0
        targetCanonicalSmoothed[i].x = avatarRig.canonical[i].x
        targetCanonicalSmoothed[i].y = avatarRig.canonical[i].y
        targetCanonicalSmoothed[i].z = avatarRig.canonical[i].z || 0
      }
    }

    const sourceCanonical =
      liveCanonical && baselineCanonical ? targetCanonicalSmoothed : targetCanonical

    // face geometry blending
    if (
      !fullMeshRetarget &&
      settings.useFaceGeometry !== false &&
      meshReady &&
      avatarRig?.canonical &&
      faceGeometryEngine
    ) {
      const geometryPose = faceGeometryEngine.estimateSimilarityTransform(
        avatarRig.canonical,
        liveCanonical,
        FACE_GEOMETRY_ANCHORS
      )
      if (geometryPose?.ok) {
        currentFaceGeometryPoseMatrix = geometryPose.matrix || null
        if (!geometryCanonicalBuffer || geometryCanonicalBuffer.length !== resolvedCount)
          geometryCanonicalBuffer = createPointArray(resolvedCount)
        const transformedCanonical = faceGeometryEngine.transformPoints(
          avatarRig.canonical,
          geometryPose,
          geometryCanonicalBuffer
        )
        if (transformedCanonical && transformedCanonical.length === resolvedCount) {
          if (!geometryCanonicalSmoothed || geometryCanonicalSmoothed.length !== resolvedCount) {
            geometryCanonicalSmoothed = createPointArray(resolvedCount)
            for (let i = 0; i < resolvedCount; i += 1) {
              geometryCanonicalSmoothed[i].x = transformedCanonical[i].x
              geometryCanonicalSmoothed[i].y = transformedCanonical[i].y
              geometryCanonicalSmoothed[i].z = transformedCanonical[i].z || 0
            }
          } else {
            const smoothingAlpha = Number.isFinite(Number(settings.geometrySmoothing))
              ? Math.min(1, Math.max(0.08, 1 - Number(settings.geometrySmoothing)))
              : settings.meshQuality === 'performance'
                ? 0.3
                : settings.meshQuality === 'high'
                  ? 0.48
                  : 0.4
            for (let i = 0; i < resolvedCount; i += 1) {
              geometryCanonicalSmoothed[i].x = lerp(
                geometryCanonicalSmoothed[i].x,
                transformedCanonical[i].x,
                smoothingAlpha
              )
              geometryCanonicalSmoothed[i].y = lerp(
                geometryCanonicalSmoothed[i].y,
                transformedCanonical[i].y,
                smoothingAlpha
              )
              geometryCanonicalSmoothed[i].z = lerp(
                geometryCanonicalSmoothed[i].z || 0,
                transformedCanonical[i].z || 0,
                smoothingAlpha
              )
            }
          }

          const baseBlend = clamp(Number(settings.faceGeometryBlend) || 0.72, 0, 1)
          const rmse = Number(geometryPose.rmse) || 0
          const rmseConfidence = clamp(1 - rmse * 8, 0.2, 1)
          const scaleConfidence = clamp(
            1 - Math.abs((Number(geometryPose.scale) || 1) - 1) * 0.7,
            0.35,
            1
          )
          const blend = baseBlend * rmseConfidence * scaleConfidence
          if (blend > 0) {
            for (let i = 0; i < resolvedCount; i += 1) {
              warpCanonicalBuffer[i].x = lerp(
                sourceCanonical[i].x,
                geometryCanonicalSmoothed[i].x,
                blend
              )
              warpCanonicalBuffer[i].y = lerp(
                sourceCanonical[i].y,
                geometryCanonicalSmoothed[i].y,
                blend
              )
              warpCanonicalBuffer[i].z = lerp(
                sourceCanonical[i].z || 0,
                geometryCanonicalSmoothed[i].z || 0,
                blend
              )
            }
            appliedFaceGeometry = true
          }
        }
      }
    }

    if (!appliedFaceGeometry) {
      for (let i = 0; i < resolvedCount; i += 1) {
        warpCanonicalBuffer[i].x = sourceCanonical[i].x
        warpCanonicalBuffer[i].y = sourceCanonical[i].y
        warpCanonicalBuffer[i].z = sourceCanonical[i].z || 0
      }
    }

    const warpCanonical = warpCanonicalBuffer

    if (
      !fullMeshRetarget &&
      meshReady &&
      liveCanonical &&
      featureWrapProfile &&
      settings.expressionWarp
    ) {
      applyFeatureAwareWrap({
        dstCanonical: warpCanonical,
        liveCanonicalPoints: liveCanonical,
        profile: featureWrapProfile,
        fitStrength: settings.faceFitStrength,
        geometryBlend: settings.faceGeometryBlend
      })
    }

    // In direct full-mesh mode, lips/jaw come from the transferred face mesh itself.
    // Keep mouth model only as fallback when live canonical is unavailable.
    const useMouthFallback = !fullMeshRetarget || !liveCanonical || !baselineCanonical
    if (useMouthFallback) {
      applyCanonicalMouthDeformation({
        points: warpCanonical,
        avatarRig,
        settings,
        jawOpen,
        mouthLevel,
        blendshapeSignals,
        liveCanonical,
        baselineCanonical
      })
    }

    let eyeSocketDstPoints = null
    if (showAvatarEyes) eyeSocketDstPoints = canonicalToCanvas(warpCanonical, liveRigMetrics, null)

    return {
      warpCanonical,
      eyeSocketDstPoints,
      faceGeometryPoseMatrix: currentFaceGeometryPoseMatrix
    }
  }

  return { computeWarp }
}

export default { createMeshWarper }
